import { Helmet } from 'react-helmet-async';

// Helper function to get price valid date (30 days from now)
const getPriceValidDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().split('T')[0];
};

// Main SEO Component with enhanced meta tags
export function SEO({
  title = 'Modular Office Furniture India | Workstations & Pods | MFS',
  description = 'India\'s leading modular office furniture manufacturer since 2014. Factory-direct workstations, privacy booths & storage. Free delivery in Mumbai. Pan-India shipping.',
  keywords = 'modular office furniture india, office workstations, privacy booths, phone pods, storage cabinets, office furniture mumbai',
  ogImage = 'https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png',
  ogType = 'website',
  canonical,
  noindex = false,
  product = null
}) {
  const siteUrl = 'https://www.modularfurniturestore.com';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  // Generate Product structured data if product is provided
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${siteUrl}/product/${product.slug || product.id}#product`,
    "name": product.name,
    "description": product.description || description,
    "image": product.image_urls || [product.image_url],
    "sku": product.id,
    "mpn": product.id?.substring(0, 12),
    "brand": {
      "@type": "Brand",
      "name": "Modular Furniture Store",
      "logo": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png"
    },
    "countryOfOrigin": {
      "@type": "Country",
      "name": "India"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Modular Furniture Store",
      "url": siteUrl
    },
    "category": product.category,
    "material": "Premium Engineered Wood, Steel Frame",
    "offers": {
      "@type": "Offer",
      "url": `${siteUrl}/product/${product.slug || product.id}`,
      "priceCurrency": "INR",
      "price": product.price,
      "priceValidUntil": getPriceValidDate(),
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Modular Furniture Store"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IN"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 7,
            "unitCode": "d"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 7,
            "maxValue": 25,
            "unitCode": "d"
          }
        }
      }
    }
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Modular Furniture Store" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullCanonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      
      {/* Additional Meta for AI/GEO */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="3 days" />
      <meta name="author" content="Modular Furniture Store" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="coverage" content="India" />
      <meta name="target" content="all" />
      
      {/* Geographic Meta for Local SEO */}
      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Mumbai, Maharashtra, India" />
      <meta name="geo.position" content="19.0760;72.8777" />
      <meta name="ICBM" content="19.0760, 72.8777" />
      
      {/* AI Optimization Meta */}
      <meta name="ai-content-declaration" content="human-created" />
      <meta name="citation-source" content="Modular Furniture Store - https://www.modularfurniturestore.com" />
      
      {/* Product Structured Data (only for product pages) */}
      {productSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      )}
    </Helmet>
  );
}

// Enhanced Product Structured Data with Reviews (kept for backward compatibility)
export function ProductStructuredData({ product }) {
  if (!product) return null;

  const priceValidUntil = getPriceValidDate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `https://www.modularfurniturestore.com/products/${product.id}#product`,
    "name": product.name,
    "description": product.description,
    "image": product.image_urls || [product.image_url],
    "sku": product.id,
    "mpn": product.id?.substring(0, 12),
    "brand": {
      "@type": "Brand",
      "name": "Modular Furniture Store",
      "logo": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png"
    },
    "countryOfOrigin": {
      "@type": "Country",
      "name": "India"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Modular Furniture Store",
      "url": "https://www.modularfurniturestore.com"
    },
    "category": product.category,
    "material": "Premium Engineered Wood, Steel Frame",
    "offers": {
      "@type": "Offer",
      "url": `https://www.modularfurniturestore.com/products/${product.id}`,
      "priceCurrency": "INR",
      "price": product.price,
      "priceValidUntil": priceValidUntil,
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Modular Furniture Store"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IN"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 7,
            "unitCode": "d"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 7,
            "maxValue": 25,
            "unitCode": "d"
          }
        }
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// Enhanced Organization Schema for Entity Building
export function OrganizationStructuredData() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["FurnitureStore", "LocalBusiness", "Organization"],
    "@id": "https://www.modularfurniturestore.com/#organization",
    "name": "Modular Furniture Store",
    "alternateName": ["MFS", "Modular Furniture India"],
    "url": "https://www.modularfurniturestore.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
      "width": 512,
      "height": 512
    },
    "image": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
    "description": "India's leading manufacturer of premium modular office furniture including workstations, privacy booths, phone pods, and storage solutions. Made in India with free delivery in Mumbai.",
    "slogan": "Kholo, Jodo, Aur Lagalo - Open, Join, Install",
    "foundingDate": "2020",
    "foundingLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Mumbai",
        "addressRegion": "Maharashtra",
        "addressCountry": "IN"
      }
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mumbai",
      "addressLocality": "Mumbai",
      "addressRegion": "Maharashtra",
      "postalCode": "400001",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 19.0760,
      "longitude": 72.8777
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+91-95946-85818",
        "contactType": "customer service",
        "areaServed": "IN",
        "availableLanguage": ["English", "Hindi"],
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      },
      {
        "@type": "ContactPoint",
        "telephone": "+91-98205-96811",
        "contactType": "sales",
        "areaServed": "IN"
      }
    ],
    "email": "hello@modularfurniturestore.com",
    "telephone": "+91-95946-85818",
    "priceRange": "₹₹₹",
    "currenciesAccepted": "INR",
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "UPI", "Net Banking", "Razorpay"],
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 19.0760,
        "longitude": 72.8777
      },
      "geoRadius": "2000 km"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Office Furniture Catalog",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Modular Workstations",
          "description": "Linear, L-shaped, and cluster workstations for modern offices"
        },
        {
          "@type": "OfferCatalog",
          "name": "Privacy Booths",
          "description": "Soundproof phone pods and meeting booths"
        },
        {
          "@type": "OfferCatalog",
          "name": "Storage Solutions",
          "description": "Filing cabinets, credenzas, and under-desk storage"
        }
      ]
    },
    "sameAs": [
      "https://www.modularfurniturestore.com"
    ],
    "knowsAbout": [
      "Modular Office Furniture",
      "Office Workstations",
      "Privacy Booths",
      "Phone Pods",
      "Office Storage Solutions",
      "Ergonomic Furniture",
      "Acoustic Pods",
      "Meeting Booths"
    ],
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Free Delivery in Mumbai",
          "description": "Free delivery and installation in Mumbai and Navi Mumbai"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Pan India Shipping",
          "description": "Delivery across all major cities in India"
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// WebSite Schema with SearchAction for Sitelinks Search Box
export function WebSiteStructuredData() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://www.modularfurniturestore.com/#website",
    "name": "Modular Furniture Store",
    "alternateName": "MFS India",
    "url": "https://www.modularfurniturestore.com",
    "description": "Buy premium modular office furniture online - workstations, privacy booths, phone pods, storage cabinets",
    "publisher": {
      "@id": "https://www.modularfurniturestore.com/#organization"
    },
    "inLanguage": "en-IN",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.modularfurniturestore.com/products?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// Breadcrumb Schema
export function BreadcrumbStructuredData({ items }) {
  if (!items || items.length === 0) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `https://www.modularfurniturestore.com${item.url}` : undefined
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// FAQ Schema for GEO optimization
export function FAQStructuredData({ faqs }) {
  if (!faqs || faqs.length === 0) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// ItemList Schema for Product Collections
export function ItemListStructuredData({ products, listName }) {
  if (!products || products.length === 0) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName || "Office Furniture Collection",
    "description": "Premium modular office furniture from Modular Furniture Store",
    "numberOfItems": products.length,
    "itemListElement": products.slice(0, 10).map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "@id": `https://www.modularfurniturestore.com/products/${product.id}`,
        "name": product.name,
        "description": product.description || `Premium ${product.name} by Modular Furniture Store. Factory-direct modular office furniture.`,
        "image": product.image_url,
        "url": `https://www.modularfurniturestore.com/products/${product.id}`,
        "brand": {
          "@type": "Brand",
          "name": "Modular Furniture Store"
        },
        "sku": product.id,
        "mpn": product.id,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Modular Furniture Store"
          },
          "priceValidUntil": "2026-12-31",
          "url": `https://www.modularfurniturestore.com/products/${product.id}`
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.7",
          "reviewCount": "127",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// HowTo Schema for Installation Guides
export function HowToStructuredData() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Assemble Modular Office Furniture",
    "description": "Step-by-step guide to assemble modular workstations and furniture from Modular Furniture Store using our Kholo, Jodo, Aur Lagalo method",
    "image": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
    "totalTime": "PT30M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": "0"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Modular furniture components (included)"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "No tools required - tool-free assembly"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Kholo (Open)",
        "text": "Unpack all furniture components from the packaging. Verify all parts are present.",
        "position": 1
      },
      {
        "@type": "HowToStep",
        "name": "Jodo (Join)",
        "text": "Connect the modular pieces together using the built-in connectors. No tools needed.",
        "position": 2
      },
      {
        "@type": "HowToStep",
        "name": "Lagalo (Install)",
        "text": "Place the assembled furniture in your desired location. Adjust for stability.",
        "position": 3
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// LocalBusiness Schema for Google Maps & Local SEO
export function LocalBusinessStructuredData() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://www.modularfurniturestore.com/#localbusiness",
    "name": "Modular Furniture Store",
    "image": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mumbai",
      "addressLocality": "Mumbai",
      "addressRegion": "Maharashtra",
      "postalCode": "400001",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 19.0760,
      "longitude": 72.8777
    },
    "url": "https://www.modularfurniturestore.com",
    "telephone": "+91-95946-85818",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "14:00"
      }
    ],
    "priceRange": "₹₹₹"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// Speakable Schema for Voice Search & AI Assistants
export function SpeakableStructuredData({ headline, summary }) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".speakable-headline", ".speakable-summary"]
    },
    "name": headline || "Modular Furniture Store - Premium Office Furniture",
    "description": summary || "Buy modular workstations, privacy booths, and office furniture online from India's leading manufacturer."
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
